// Detection + escape hatches for social-media in-app browsers (webviews).
//
// Instagram's iOS webview silently blocks navigations to apps.apple.com, so a
// plain `window.location.href = storeUrl` on /download does nothing there.
// Instagram exposes an internal scheme that hands a URL to the system browser,
// which it honours instead of blocking: instagram://extbrowser/?url=<encoded>

const ua = (): string => (typeof navigator !== 'undefined' ? navigator.userAgent : '');

export const isIOS = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ reports a desktop Macintosh UA, so treat touch-capable Macs as iOS.
  return /iPhone|iPad|iPod/.test(ua())
    || (/Macintosh/.test(ua()) && typeof document !== 'undefined' && 'ontouchend' in document);
};

export const isInstagramBrowser = (): boolean => /Instagram/i.test(ua());

// True only where the extbrowser escape hatch is both needed and available.
export const isIOSInstagramBrowser = (): boolean => isIOS() && isInstagramBrowser();

export const getInstagramExternalBrowserUrl = (targetUrl: string): string =>
  `instagram://extbrowser/?url=${encodeURIComponent(targetUrl)}`;

/**
 * Attempts to hand `targetUrl` to the system browser when running inside the
 * iOS Instagram webview. Returns true if the escape hatch was triggered, so
 * callers can skip their normal (blocked) redirect and show a manual fallback.
 */
export const openInExternalBrowser = (targetUrl: string): boolean => {
  if (!isIOSInstagramBrowser()) return false;
  window.location.href = getInstagramExternalBrowserUrl(targetUrl);
  return true;
};

/**
 * Props for an <a> pointing at an app-store URL. Inside the iOS Instagram
 * webview the link is rewritten to the extbrowser scheme and target="_blank"
 * is dropped — a custom scheme opened in a new webview tab goes nowhere.
 */
export const storeLinkProps = (url: string): { href: string; target?: string; rel?: string } =>
  isIOSInstagramBrowser()
    ? { href: getInstagramExternalBrowserUrl(url) }
    : { href: url, target: '_blank', rel: 'noopener noreferrer' };

/** Programmatic equivalent of storeLinkProps, for click handlers. */
export const openStoreUrl = (url: string): void => {
  if (isIOSInstagramBrowser()) {
    window.location.href = getInstagramExternalBrowserUrl(url);
    return;
  }
  window.open(url, '_blank');
};
